import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { LogoMark } from "./Logo";
import { Footer } from "./Footer";

// Shared shell for legal / support / contact pages.
// Provides a minimal header (logo + back-to-site) and the site-wide Footer.
// Keeps typography and spacing consistent with the marketing website.
export const PageShell = ({ eyebrow, title, testId, children }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = `${title} — MiseOne`;
    return () => {
      document.title = "MiseOne — The Digital Mise en Place";
    };
  }, [title]);

  return (
    <div className="relative min-h-screen overflow-x-clip bg-[#f5f2ea]">
      <header className="mx-auto flex w-full max-w-[1600px] items-center justify-between px-6 py-6 md:px-12">
        <Link to="/" className="inline-flex items-center gap-3" data-testid="page-shell-home">
          <LogoMark size={30} />
          <span className="font-editorial text-2xl tracking-tight text-[#1b1a18]">Mise One</span>
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full border border-[var(--mo-line)] bg-white/60 px-5 py-2 font-body text-[13px] font-medium text-[#1b1a18] transition-colors hover:bg-white"
          data-testid="page-shell-back"
        >
          <ArrowLeft size={14} strokeWidth={1.8} />
          Back to site
        </Link>
      </header>

      <main
        data-testid={testId}
        className="mx-auto w-full max-w-[880px] px-6 pt-10 pb-24 md:px-12 md:pt-16 md:pb-32"
      >
        <span className="font-body text-[11px] uppercase tracking-[0.32em] text-[#EA6A43]">
          {eyebrow}
        </span>
        <h1 className="mt-5 font-display text-5xl font-black leading-[0.95] tracking-tighter text-[#1b1a18] sm:text-6xl">
          {title}
        </h1>

        <div className="mt-14 space-y-12 font-body text-[16px] leading-[1.75] text-[#4b463e]">
          {children}
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Small typographic helpers so every page renders headings/paragraphs identically.
export const Section = ({ heading, children }) => (
  <section>
    <h2 className="font-display text-2xl font-bold tracking-tight text-[#1b1a18] sm:text-3xl">
      {heading}
    </h2>
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

export const Bullets = ({ items }) => (
  <ul className="mt-2 space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex gap-3">
        <span className="mt-[10px] inline-block h-[5px] w-[5px] flex-none rounded-full bg-[#EA6A43]" />
        <span>{it}</span>
      </li>
    ))}
  </ul>
);
